import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UserStateService } from './user-state.service';
import { AppStateService } from './app-state.service';
import { GlobalsService } from './globals.service';
import { EditorStateService } from './editor-state.service';
import { PermissionsService } from './permissions.service';
import { ApiService } from './api.service';
import { UserService } from './user.service';
import { AlertService } from './alert.service';
import { AppState } from '../models';
import { SessionExpiredDialogComponent } from '../../shared/components/session-expired-dialog/session-expired-dialog.component';
import { SessionSupersededDialogComponent } from '../../shared/components/session-superseded-dialog/session-superseded-dialog.component';

/**
 * SessionService - Manages user session state and expiration
 * 
 * This service:
 * - Listens for session expiration events (ERROR_101 from AMF)
 * - Shows a dialog when session expires
 * - Handles logout and redirect to login
 * - Prevents duplicate session expired dialogs
 */
@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly userState = inject(UserStateService);
  private readonly appState = inject(AppStateService);
  private readonly globals = inject(GlobalsService);
  private readonly editorState = inject(EditorStateService);
  private readonly permissions = inject(PermissionsService);
  // Static import is one-directional (api.service reaches back here only via
  // dynamic import), so no DI/module cycle.
  private readonly api = inject(ApiService);
  // Same one-directionality holds for UserService: it never imports
  // session.service statically, so this edge closes no cycle.
  private readonly userService = inject(UserService);
  private readonly alert = inject(AlertService);
  private readonly translate = inject(TranslateService);

  // Subject for session expiration events
  private readonly sessionExpired$ = new Subject<string>();

  // Track if we're already showing the session expired dialog
  private isShowingExpiredDialog = signal(false);
  private expiredDialogRef: MatDialogRef<SessionExpiredDialogComponent> | null = null;

  // Session superseded state
  private readonly _isSessionSuperseded = signal(false);
  readonly isSessionSuperseded = this._isSessionSuperseded.asReadonly();
  private isShowingSupersededDialog = signal(false);
  
  constructor() {
    // Subscribe to session expiration events
    this.sessionExpired$.subscribe(reason => {
      this.handleSessionExpired(reason);
    });
  }
  
  /**
   * Called when ERROR_101 is detected from AMF response
   */
  onSessionExpired(reason: string = 'ERROR_101'): void {
    // Only trigger if user is logged in
    if (!this.userState.isLoggedIn()) {
      return;
    }
    
    this.sessionExpired$.next(reason);
  }

  /**
   * Called on a 401 with a session-ending code other than superseded
   * (sub_user_disabled / account_disabled / customer_users_disabled —
   * customer-users guide §3.6). Shows the expired dialog with a per-code
   * message, then logs out.
   */
  onSessionTerminated(code: string): void {
    // Deliberately NOT gated on isLoggedIn(): the disable codes also arrive
    // on the boot-time GET /users/me (before the user signal is set), and the
    // reload is exactly how most people discover a killed session — the
    // per-code dialog must still show then.
    const messageKey =
      code === 'sub_user_disabled' ? 'SESSION_TERMINATED.SUB_USER_DISABLED'
      : code === 'account_disabled' ? 'SESSION_TERMINATED.ACCOUNT_DISABLED'
      : code === 'customer_users_disabled' ? 'SESSION_TERMINATED.TEAM_DISABLED'
      : undefined;
    this.handleSessionExpired(code, messageKey);
  }

  /**
   * Handle session expiration - show dialog and logout
   */
  private handleSessionExpired(reason: string, messageKey?: string): void {
    // Prevent duplicate dialogs
    if (this.isShowingExpiredDialog()) {
      return;
    }

    // While impersonating, an expired/terminated ACTIVE session means the
    // SUPPORT session died (impersonation JWTs are access-token-only — 1h
    // TTL, no refresh token, by backend design), not the admin's own
    // session. Swap back to the parked admin instead of logging out.
    if (this.userState.isSupportSession() && this.recoverFromExpiredImpersonation()) {
      return;
    }

    console.log('[SessionService] Session expired:', reason);
    this.isShowingExpiredDialog.set(true);

    // Show the session expired dialog
    this.expiredDialogRef = this.dialog.open(SessionExpiredDialogComponent, {
      width: '400px',
      disableClose: true, // User must acknowledge
      panelClass: 'confirm-dialog-panel',
      data: { reason, messageKey }
    });
    
    this.expiredDialogRef.afterClosed().subscribe(() => {
      this.isShowingExpiredDialog.set(false);
      this.expiredDialogRef = null;
      this.performLogout();
    });
  }
  
  /**
   * Perform logout and redirect to login screen
   */
  private performLogout(): void {
    console.log('[SessionService] Performing logout after session expiration');

    // Reset superseded flag
    this._isSessionSuperseded.set(false);

    // Clear user state but preserve remembered user info for login form
    this.userState.clear();
    this.userState.setUser(null);
    this.permissions.clear();
    // Drop the persisted tokens too (guide §3.6: "clears tokens and routes to
    // login"). Critical for the terminated codes (sub_user_disabled etc.):
    // the access token is still VALID for lenient reads, so a reload with the
    // tokens left in localStorage would restore the killed session.
    this.api.clearTokens();
    this.globals.setUserType(0);
    // Manual hide-prices state must not leak into the next session (a
    // sub-user session has no toggle to undo it).
    this.appState.resetHidePrices();
    sessionStorage.removeItem('postLoginRedirect');
    
    // Remove keepSession flag so auto-login doesn't trigger on reload
    // BUT keep credentials and localUser so the login form is pre-filled
    // The user just needs to click login again (session expired, not password changed)
    localStorage.removeItem('keepSession');
    
    // Keep credentials in userState - they're still valid, just session expired
    // Only clear keepSession flag so auto-login doesn't loop
    this.userState.setKeepSession(false);
    
    // Set app state to LOGIN
    this.appState.setState(AppState.LOGIN);
    
    // Navigate to login route
    this.router.navigate(this.appState.buildLocaleCommands(['login']));
  }
  
  /**
   * Restore the parked admin when the impersonation token dies mid-session.
   * Same routine as the banner's manual exit (token swap, cart restore,
   * admin post-login refetch), then lands the admin on home with a toast
   * explaining what happened. Returns false when there is no parked admin
   * to restore, so the caller falls through to the normal logout flow.
   *
   * If the admin's own tokens are dead too, the post-login refetches 401,
   * their refresh fails, and we re-enter handleSessionExpired — this time
   * with isSupportSession() false — landing on the normal logout dialog.
   */
  private recoverFromExpiredImpersonation(): boolean {
    const admin = this.userService.exitSupportSession();
    if (!admin) return false;
    console.log('[SessionService] Impersonation token expired — restored parked admin session');
    this.alert.warning(this.translate.instant('TOPBAR.IMPERSONATION_EXPIRED_RESTORED'), 8000);
    this.router.navigate(this.appState.buildLocaleCommands(['home']));
    return true;
  }

  /**
   * Check if session expired dialog is currently showing
   */
  isDialogShowing(): boolean {
    return this.isShowingExpiredDialog();
  }

  // ─── Session Superseded ───

  /**
   * Called when a 401 with { error: "session_superseded" } is detected.
   * The user logged in on another device — this session can no longer write.
   */
  onSessionSuperseded(): void {
    if (!this.userState.isLoggedIn()) return;
    if (this._isSessionSuperseded()) return;
    this._isSessionSuperseded.set(true);
    this.handleSessionSuperseded();
  }

  private handleSessionSuperseded(): void {
    if (this.isShowingSupersededDialog()) return;

    console.log('[SessionService] Session superseded — another device logged in');
    this.isShowingSupersededDialog.set(true);

    const hasUnsavedWork = this.editorState.hasProject();

    const dialogRef = this.dialog.open(SessionSupersededDialogComponent, {
      width: '450px',
      disableClose: true,
      panelClass: 'confirm-dialog-panel',
      data: { hasUnsavedWork }
    });

    dialogRef.afterClosed().subscribe(async (result: string) => {
      this.isShowingSupersededDialog.set(false);
      if (result === 'save_and_login') {
        await this.saveEditorWorkLocally();
      }
      this.performLogout();
    });
  }

  private async saveEditorWorkLocally(): Promise<void> {
    try {
      const projectInfo = this.editorState.projectInfo();
      const projectModel = this.editorState.projectModel();
      if (!projectInfo && !projectModel) return;

      const emergencyDraft = {
        savedAt: new Date().toISOString(),
        projectInfo,
        projectModel
      };
      localStorage.setItem('express_emergency_draft', JSON.stringify(emergencyDraft));
      console.log('[SessionService] Emergency draft saved to localStorage');
    } catch (e) {
      console.warn('[SessionService] Failed to save emergency draft:', e);
    }
  }
}
